/**
 * Production environment.
 *
 * Reads runtime configuration from `window.__env` (injected by entrypoint.sh at
 * container startup / by scripts/prepare-env.js for build:prod). No hardcoded
 * placeholders: every environment-dependent value comes exclusively from env
 * vars, wired ConfigMap -> entrypoint.sh -> window.__env. Defaults declared in
 * the kustomize base ConfigMap (S57_STYLE_URL, WHISPER_*, IA_MODEL_DOWNLOAD_URL,
 * ...) are inherited by every deploy env via `behavior: merge`, so no S3 URL is
 * duplicated here. The only literals left are non-environmental constants
 * (serviceWorker timings), prod-safe empty/boolean/logLevel fallbacks, and
 * `copernicusWmtsBase` — a stable public third-party WMTS endpoint whose public
 * URL is the fallback, overridable at runtime via window.__env (hqdp-ui#851/852).
 *
 * This is the base file (no fileReplacement): both the `production` and
 * `prod-debug` build configurations use it — they differ only in build flags
 * (minification, sourcemaps, chunk naming), not in environment values.
 */
export const environment = {
  production: true,
  siteIdentifier: window.__env?.siteIdentifier,
  hqdpApiUrl: window.__env?.hqdpApiUrl,
  casoarApiUrl: window.__env?.casoarApiUrl,
  vectorStyleUrl: window.__env?.vectorStyleUrl,
  satelliteStyleUrl: window.__env?.satelliteStyleUrl,
  s57StyleUrl: window.__env?.s57StyleUrl,
  signalingUrls: window.__env?.signalingUrls ? JSON.parse(window.__env.signalingUrls) : [],

  stunServers: window.__env?.stunServers ? JSON.parse(window.__env.stunServers) : [],

  // whether to enable local persistence (indexeddb and localStorage)
  enablePersistence: window.__env?.enablePersistence !== undefined ? window.__env.enablePersistence === true : true,

  // Service Worker configuration
  serviceWorker: {
    enabled: true, // Enable/disable PWA services worker for automatic updates (enabled in production)
    updateCheckInterval: 60 * 60 * 1000, // How often to check for updates (1 hour = 60*60*1000ms)
    registrationStrategy: 'registerWhenStable:300', // Wait for app to be stable, max 30 seconds
  },

  // Whisper STT (single multilingual HF-repo zip, lazy-loaded on first use).
  // WHISPER_FRENCH_URL / WHISPER_ENGLISH_URL come from the ConfigMap (base default).
  whisper: window.__env?.whisper ? JSON.parse(window.__env.whisper) : undefined,
  enableHexaMesh: window.__env?.enableHexaMesh !== undefined ? window.__env.enableHexaMesh : false,

  // Hocuspocus server URL(s) for backend sync, if multiple sites this has to be coma separated list,
  // if this is a single site, it should be a single URL
  // Important :If we have more than one site we must disable the remote site on the realtime server configuration
  hocuspocusServerUrl: window.__env?.hocuspocusServerUrl,

  keycloak: window.__env?.keycloak ? JSON.parse(window.__env.keycloak) : {},
  iaAlgorithmsUrl: window.__env?.iaAlgorithmsUrl,
  iaAgentMeshUrl: window.__env?.iaAgentMeshUrl,
  copernicusWmtsBase: window.__env?.copernicusWmtsBase || 'https://wmts.marine.copernicus.eu/teroWmts', // public URL as fallback, overridable at runtime
  terrainPmtilesUrl: window.__env?.terrainPmtilesUrl, // PMTiles file for terrain elevation (provided by every deploy env)

  // DCS (Data Centric Security) configuration
  dcs: {
    enabled: window.__env?.dcsEnabled ?? true, // Enable DCS features (security labeling on import/export)
    lesUrl: window.__env?.dcsLesUrl, // DCS Labeling External Service (LES) API URL
    spifCacheMaxAgeMs: window.__env?.spifCacheMaxAgeMs ?? 24 * 60 * 60 * 1000, // SPIF cache validity duration (default: 24 hours)
  },

  // Download repo for ai agent models (IA_MODEL_DOWNLOAD_URL, base ConfigMap default)
  iaModelDownloadUrl: window.__env?.iaModelDownloadUrl,
  // Full URL of the local Gemma model, built from the base download repo above.
  iaModelUrl: `${window.__env?.iaModelDownloadUrl}/gemma-4-E4B-it-litert-lm/gemma-4-E4B-it-web.litertlm`,

  // Logging: prod default 'warn' (aligned with LOG_LEVEL:-warn in entrypoint.sh);
  // window.__env.logLevel wins at deploy time.
  logLevel: window.__env?.logLevel || 'warn',
  locale: window.__env?.locale || 'en-US',
};
